<!-- Display twin property values -->
<template lang="pug">
smx-form
  smx-form-item(
    v-for="prop in twin.properties",
    :key="prop.uuid",
    :label="prop.name",
    :required="prop.constraint?.settings?.mandatory || false"
  )
    TwinPropertyInput(
      v-if="editMode",
      :prop="prop",
      :disabled="isEditDisabled(prop)",
      :model-value="mergedInputs[prop.uuid]",
      @update:model-value="(value) => (inputs[prop.uuid] = value)"
    )

    template(v-else)
      slot(
        v-if="hasSlot(prop.customId)",
        :name="prop.customId",
        v-bind="{ property: prop, value: propertyObject[prop.uuid], twin: props.twin }"
      ) 
      TwinPropertyView(:prop="prop", :model-value="propertyObject[prop.uuid]")
  template(#actions)
    el-button(
      v-if="editMode",
      type="primary",
      :disabled="!isSaveAvailable",
      @click="save"
    ) {{ $t("save") }}
</template>

<script lang="ts" setup>
import {
  type TwinProperty,
  type Property,
  type TwinResponse,
} from "@shared/services/twinManagement";
import TwinPropertyView from "./TwinPropertyView.vue";
import { computed, ref, useSlots } from "vue";
import TwinPropertyInput from "./TwinPropertyInput.vue";

const props = withDefaults(
  defineProps<{
    twin: TwinResponse;
    editMode?: boolean;
  }>(),
  {
    editMode: false,
  },
);

const emit = defineEmits(["save"]);

const propertyObject = computed(() =>
  props.twin?.properties.reduce((agg, curr) => {
    agg[curr.uuid] = curr.propertyValue?.value;
    return agg;
  }, {}),
);

const slots = useSlots();

function hasSlot(name) {
  return !!slots[name];
}

const inputs = ref<Record<string, any>>({});

// Merge of the user inputs and the current property values
const mergedInputs = computed(() => ({
  ...propertyObject.value,
  ...inputs.value,
}));

function isEditDisabled(prop: Property) {
  return prop.constraint?.settings.notUserEditable;
}

function getTwinValuesForSaving(
  property: TwinProperty,
  value: string | Array<{ uuid: string }> | string[] | boolean,
): string | string[] | number {
  if (typeof value === "boolean") {
    return value.toString();
  }
  if (Array.isArray(value) && value.length > 0) {
    if (property.type === "twinRelation") {
      return value.map((item) => (typeof item === "string" ? item : item.uuid));
    } else {
      // Currently only twinRelation supports array, in case of a user array
      return value as string[];
    }
  }
  // NOTE: typescript doesn't check the `typeof if` clause earlier
  return value as string;
}

function save() {
  if (!isSaveAvailable.value) {
    return;
  }
  // NOTE: input values might have different types or even be null, which is not accepted by the backend.
  // Thus before saving we have to adjust types and remove null values
  const inputValuesForSaving = Object.keys(inputs.value)
    // Filter undefined values
    .filter((key) => inputs.value[key] !== undefined)
    // Filter fields not editable by the user
    .map((propId) => {
      const property = props.twin.properties.find(
        (prop) => prop.uuid === propId,
      );
      const value = inputs.value[propId];

      // For float properties, send null when value is empty to avoid backend validation errors
      if (
        property?.type === "float" &&
        (value === null || value === "" || value === undefined)
      ) {
        return {
          propertyUuid: propId,
          value: null,
        };
      }

      return {
        // NOTE: due to https://github.com/inctec/smx-portal/issues/146 null values are replaced with empty strings for non-float properties
        propertyUuid: propId,
        value: getTwinValuesForSaving(property, value !== null ? value : ""),
      };
    });

  emit("save", inputValuesForSaving);
}

const isSaveAvailable = computed(() => {
  return requiredMandatoryFields.value.length === 0;
});

// returns an array of all mandatory fields that are not filled in.
const requiredMandatoryFields = computed(() => {
  const requiredFields = [];
  (props.twin?.properties || [])
    .filter((item) => item.constraint?.settings?.mandatory)
    .forEach((item) => {
      const value = mergedInputs.value[item.uuid];
      if (value === undefined || value === null || value === "") {
        requiredFields.push(item.name);
      }
    });
  return requiredFields;
});
</script>
